import apiClient from "../client";

// =============================================
// HELPER FUNCTIONS
// =============================================

/**
 * Extract data array from API response
 * Handles nested response structures like:
 * - response.data.data (array)
 * - response.data.data.data (array)
 * - response.data (array)
 */
const extractData = (response) => {
  // Check for paginated response with data.data
  if (response?.data?.data?.data && Array.isArray(response.data.data.data)) {
    return response.data.data.data;
  }
  // Check for response.data.data (array)
  if (response?.data?.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  // Check for response.data (array)
  if (response?.data && Array.isArray(response.data)) {
    return response.data;
  }
  // Check if response itself is array
  if (Array.isArray(response)) {
    return response;
  }
  return [];
};

/**
 * Extract single item from API response
 */
const extractSingle = (response) => {
  if (response?.data?.data) {
    return response.data.data;
  }
  if (response?.data) {
    return response.data;
  }
  return response;
};

/**
 * Extract stats from API response
 */
const extractStats = (response) => {
  if (response?.data?.data) {
    return response.data.data;
  }
  if (response?.data) {
    return response.data;
  }
  return response;
};

/**
 * Extract pagination info from API response
 */
const extractPagination = (response) => {
  if (response?.data?.data?.pagination) {
    return response.data.data.pagination;
  }
  if (response?.data?.pagination) {
    return response.data.pagination;
  }
  return {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  };
};

// =============================================
// CONNECTION CRUD OPERATIONS
// =============================================

/**
 * Get all connections with optional filters
 * @param {Object} filters - Filter parameters (status, gas_type, search, etc.)
 * @returns {Promise<Object>} { data: Array, pagination: Object }
 */
export const getConnections = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        params.append(key, filters[key]);
      }
    });

    const response = await apiClient.get(`/connections${params.toString() ? `?${params.toString()}` : ''}`);
    console.log("Get connections response:", response);
    
    return {
      data: extractData(response),
      pagination: extractPagination(response),
      raw: response.data,
    };
  } catch (error) {
    console.error("Error fetching connections:", error);
    return {
      data: [],
      pagination: { page: 1, limit: 10, total: 0, totalPages: 0 },
    };
  }
};

/**
 * Get connection statistics
 * @returns {Promise<Object>} Statistics data
 */
export const getConnectionStats = async () => {
  try {
    const response = await apiClient.get('/connections/stats');
    console.log("Get connection stats response:", response);
    return extractStats(response);
  } catch (error) {
    console.error("Error fetching connection stats:", error);
    return {};
  }
};

/**
 * Get a single connection by ID
 * @param {string|number} id - Connection ID
 * @returns {Promise<Object>} Connection details
 */
export const getConnectionById = async (id) => {
  try {
    const response = await apiClient.get(`/connections/${id}`);
    return extractSingle(response);
  } catch (error) {
    console.error("Error fetching connection by ID:", error);
    throw error;
  }
};

/**
 * Get a connection by connection number
 * @param {string} connectionNumber - Connection number
 * @returns {Promise<Object>} Connection details
 */
export const getConnectionByNumber = async (connectionNumber) => {
  try {
    const response = await apiClient.get(`/connections/number/${connectionNumber}`);
    return extractSingle(response);
  } catch (error) {
    console.error("Error fetching connection by number:", error);
    throw error;
  }
};

/**
 * Create a new connection
 * @param {Object} connectionData - Connection data
 * @returns {Promise<Object>} Created connection
 */
export const createConnection = async (connectionData) => {
  try {
    console.log("📦 Creating connection with data:", connectionData);

    const payload = {
      customer_id: connectionData.customerId,
      connection_number: connectionData.connectionNumber || null,
      gas_type: connectionData.gasType || "lpg",
      cylinder_capacity: connectionData.cylinderCapacity || "14.2kg",
      cylinder_type_id: connectionData.cylinderTypeId || null,
      brand_id: connectionData.brandId || null,
      connection_date: connectionData.connectionDate,
      security_deposit: Number(connectionData.securityDeposit) || 0,
      regulator_number: connectionData.regulatorNumber || null,
      regulator_type: connectionData.regulatorType || null,
      stove_details: connectionData.stoveDetails || null,
      stove_brand: connectionData.stoveBrand || null,
      stove_model: connectionData.stoveModel || null,
      stove_type: connectionData.stoveType || "double",
      pipe_length: connectionData.pipeLength || null,
      pipe_type: connectionData.pipeType || null,
      installation_engineer: connectionData.installationEngineer || null,
      installation_date: connectionData.installationDate || null,
      installation_notes: connectionData.installationNotes || null,
      connection_status: connectionData.connectionStatus || "active",
      created_by: connectionData.createdBy || null,
    };

    console.log("📤 Sending connection payload:", JSON.stringify(payload, null, 2));
    const response = await apiClient.post("/connections", payload);
    console.log("✅ Create connection response:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error creating connection:", error);
    throw error;
  }
};

/**
 * Update an existing connection
 * @param {string|number} id - Connection ID
 * @param {Object} connectionData - Updated connection data
 * @returns {Promise<Object>} Updated connection
 */
export const updateConnection = async (id, connectionData) => {
  try {
    console.log(`📦 Updating connection ${id}:`, connectionData);
    const response = await apiClient.put(`/connections/${id}`, connectionData);
    return response.data;
  } catch (error) {
    console.error("Error updating connection:", error);
    throw error;
  }
};

/**
 * Delete a connection
 * @param {string|number} id - Connection ID
 * @returns {Promise<Object>} Deletion response
 */
export const deleteConnection = async (id) => {
  try {
    const response = await apiClient.delete(`/connections/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting connection:", error);
    throw error;
  }
};

// =============================================
// CONNECTION STATUS MANAGEMENT
// =============================================

/**
 * Activate a connection
 * @param {string|number} id - Connection ID
 * @returns {Promise<Object>} Activation response
 */
export const activateConnection = async (id) => {
  try {
    const response = await apiClient.patch(`/connections/${id}/activate`);
    return response.data;
  } catch (error) {
    console.error("Error activating connection:", error);
    throw error;
  }
};

/**
 * Suspend a connection
 * @param {string|number} id - Connection ID
 * @param {Object} suspendData - Suspension data (reason, etc.)
 * @returns {Promise<Object>} Suspension response
 */
export const suspendConnection = async (id, suspendData = {}) => {
  try {
    const response = await apiClient.patch(`/connections/${id}/suspend`, suspendData);
    return response.data;
  } catch (error) {
    console.error("Error suspending connection:", error);
    throw error;
  }
};

/**
 * Transfer a connection to a new customer
 * @param {string|number} id - Connection ID
 * @param {Object} transferData - Transfer data (customer_id, etc.)
 * @returns {Promise<Object>} Transfer response
 */
export const transferConnection = async (id, transferData) => {
  try {
    const response = await apiClient.post(`/connections/${id}/transfer`, transferData);
    return response.data;
  } catch (error) {
    console.error("Error transferring connection:", error);
    throw error;
  }
};

/**
 * Terminate a connection
 * @param {string|number} id - Connection ID
 * @param {Object} terminateData - Termination data (reason, etc.)
 * @returns {Promise<Object>} Termination response
 */
export const terminateConnection = async (id, terminateData = {}) => {
  try {
    const response = await apiClient.patch(`/connections/${id}/terminate`, terminateData);
    return response.data;
  } catch (error) {
    console.error("Error terminating connection:", error);
    throw error;
  }
};

// =============================================
// CONNECTION HISTORY
// =============================================

/**
 * Get connection history
 * @param {string|number} id - Connection ID
 * @param {Object} params - Query parameters (page, limit, etc.)
 * @returns {Promise<Array>} Connection history
 */
export const getConnectionHistory = async (id, params = {}) => {
  try {
    const queryParams = new URLSearchParams(params);
    const response = await apiClient.get(`/connections/${id}/history${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
    return extractData(response);
  } catch (error) {
    console.error("Error fetching connection history:", error);
    return [];
  }
};

/**
 * Get connection transfer history
 * @param {string|number} id - Connection ID
 * @returns {Promise<Array>} Transfer history
 */
export const getConnectionTransfers = async (id) => {
  try {
    const response = await apiClient.get(`/connections/${id}/transfers`);
    return extractData(response);
  } catch (error) {
    console.error("Error fetching connection transfers:", error);
    return [];
  }
};

/**
 * Get connections for a specific customer
 * @param {string|number} customerId - Customer ID
 * @returns {Promise<Array>} Customer's connections
 */
export const getCustomerConnections = async (customerId) => {
  try {
    const response = await apiClient.get(`/customers/${customerId}/connections`);
    return extractData(response);
  } catch (error) {
    console.error("Error fetching customer connections:", error);
    return [];
  }
};

// =============================================
// BULK OPERATIONS
// =============================================

/**
 * Bulk update connection status
 * @param {Array} ids - Array of connection IDs
 * @param {string} status - New status
 * @returns {Promise<Object>} Bulk update response
 */
export const bulkUpdateConnectionStatus = async (ids, status) => {
  try {
    const response = await apiClient.patch('/connections/bulk/status', {
      ids,
      status
    });
    return response.data;
  } catch (error) {
    console.error("Error bulk updating connections:", error);
    throw error;
  }
};

/**
 * Bulk delete connections
 * @param {Array} ids - Array of connection IDs
 * @returns {Promise<Object>} Bulk delete response
 */
export const bulkDeleteConnections = async (ids) => {
  try {
    const response = await apiClient.delete('/connections/bulk', {
      data: { ids }
    });
    return response.data;
  } catch (error) {
    console.error("Error bulk deleting connections:", error);
    throw error;
  }
};

// =============================================
// EXPORT ALL
// =============================================

export default {
  // CRUD Operations
  getConnections,
  getConnectionStats,
  getConnectionById,
  getConnectionByNumber,
  createConnection,
  updateConnection,
  deleteConnection,
  
  // Status Management
  activateConnection,
  suspendConnection,
  transferConnection,
  terminateConnection,
  
  // History
  getConnectionHistory,
  getConnectionTransfers,
  
  // Customer
  getCustomerConnections,
  
  // Bulk Operations
  bulkUpdateConnectionStatus,
  bulkDeleteConnections,
};